import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { PiPlus, PiCalendar } from 'react-icons/pi';
import { useAgendaTasksList } from '../../hooks/api/agenda-tasks-list';
import { useAgendaTasksCalendar } from '../../hooks/api/agenda-tasks-calendar';
import { TaskFilters } from '../organisms/task-filters';
import { TasksList } from '../organisms/tasks-list';
import { TasksCalendar } from '../organisms/tasks-calendar';
import { OverdueTasksIndicator } from '../molecules/overdue-tasks-indicator';
import type { TaskFiltersState } from '../../types/agenda.types';
import { ButtonSkeleton } from 'src/modules/shared/components/atoms/buttons/button-skeleton';
import { useOverdueTasksCount } from '../../hooks/overdue-tasks-count';
import { NewTaskForm } from 'src/modules/shared/components/molecules/tasks/new-task-form';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { Route as CalendarRoute } from 'src/routes/calendar';
import { calendarSearchSchema } from 'src/modules/shared/schemas/calendar.schema';
import { AgendaSkeleton } from '../molecules/agenda-skeleton';
import { InfiniteScrollTrigger } from '../molecules/infinite-scroll-trigger';
import type { CalendarDateRange } from 'src/modules/calendar/utils/date.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

// The page still owns two view modes plus task-creation state; keeping it
// local is preferable to an unrelated refactor in this UI-standardization pass.
// eslint-disable-next-line sonarjs/cognitive-complexity
export const AgendaPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { view } = useSearch({ from: CalendarRoute.id });
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [filters, setFilters] = useState<TaskFiltersState>({});
  const [calendarDateRange, setCalendarDateRange] =
    useState<CalendarDateRange | null>(null);

  const listResult = useAgendaTasksList({
    filters,
    enabled: view === 'list',
  });

  const calendarResult = useAgendaTasksCalendar({
    filters,
    enabled: view === 'calendar' && !!calendarDateRange,
    startDate: calendarDateRange?.startDate ?? '',
    endDate: calendarDateRange?.endDate ?? '',
  });

  const currentResult = view === 'list' ? listResult : calendarResult;

  const { tasks, isLoading, error, refetch } = currentResult;
  const isFetching =
    'isFetching' in currentResult ? currentResult.isFetching : false;
  const loadingMore =
    'loadingMore' in currentResult ? currentResult.loadingMore : false;
  const loadMore =
    'loadMore' in currentResult ? currentResult.loadMore : undefined;
  const hasNextPage =
    'hasNextPage' in currentResult ? currentResult.hasNextPage : false;

  const overdueTasksCount = useOverdueTasksCount(tasks);

  const handleCreateTask = () => {
    setShowNewTaskForm(true);
  };

  const handleFiltersChange = (newFilters: TaskFiltersState) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleViewChange = (newView: 'list' | 'calendar') => {
    void navigate({
      to: CalendarRoute.to,
      search: (prev) => {
        const parsedSearch = calendarSearchSchema.parse(prev);
        return {
          ...parsedSearch,
          view: newView,
        };
      },
    });
  };

  const handleCalendarDateRangeChange = (startDate: Date, endDate: Date) => {
    setCalendarDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    });
  };

  const headerActions = isLoading ? (
    <TextSkeleton className="h-9 w-32" />
  ) : (
    <Button
      onClick={handleCreateTask}
      variant="primary"
      appearance="filled"
      size="md"
      aria-label={t('calendar.agenda.actions.newTask')}
      className="inline-flex self-center items-center justify-center gap-2"
    >
      <PiPlus className="w-4 h-4 flex-shrink-0" />
      <span className="hidden sm:inline whitespace-nowrap">
        {t('calendar.agenda.actions.newTask')}
      </span>
    </Button>
  );

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader
          className="px-4 py-3 border-b gap-0"
          containerClassName="gap-4"
          actions={headerActions}
        >
          {isLoading ? (
            <TextSkeleton className="h-10 w-[12rem]" />
          ) : (
            <div className="flex items-center gap-4">
              <PiCalendar className="w-7 h-7 flex-shrink-0" />
              <Text as="h2" size="h2" weight="medium">
                {t('calendar.agenda.title')}
              </Text>

              {overdueTasksCount > 0 && (
                <OverdueTasksIndicator count={overdueTasksCount} />
              )}
            </div>
          )}
        </PanelHeader>

        {isLoading ? (
          <PageFiltersBar>
            <ButtonSkeleton variant="primary" size="md" />
            <ButtonSkeleton variant="neutral" size="md" />
            <ButtonSkeleton variant="secondary" size="md" />
          </PageFiltersBar>
        ) : (
          <TaskFilters
            currentView={view}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            onViewChange={handleViewChange}
          />
        )}

        <div className="flex-1 overflow-auto p-4">
          <div className="max-w-6xl mx-auto h-full relative">
            {showNewTaskForm && (
              <div className="mb-6 border-b border-gray-100 pb-4">
                <NewTaskForm
                  showConversationField
                  onTaskCreated={() => setShowNewTaskForm(false)}
                  onTaskCancel={() => setShowNewTaskForm(false)}
                />
              </div>
            )}

            {view === 'list' ? (
              isLoading ? (
                <AgendaSkeleton view={view} />
              ) : error ? (
                <div className="h-full flex items-center justify-center">
                  <ErrorState
                    title={t('calendar.agenda.error.title')}
                    description={t('calendar.agenda.error.description')}
                    onAction={() => void refetch()}
                    actionContent={t('calendar.agenda.error.retry')}
                  />
                </div>
              ) : (
                <div className="h-full flex flex-col gap-4 pb-4">
                  <TasksList tasks={tasks} onCreateTask={handleCreateTask} />

                  <InfiniteScrollTrigger
                    onLoadMore={loadMore ?? (() => undefined)}
                    hasMore={Boolean(hasNextPage)}
                    loading={loadingMore}
                  >
                    {loadingMore && (
                      <div className="flex justify-center py-4">
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                          <Text size="small">
                            {t('calendar.agenda.loadingMore')}
                          </Text>
                        </div>
                      </div>
                    )}
                  </InfiniteScrollTrigger>
                </div>
              )
            ) : (
              <div className="h-full flex flex-col gap-4">
                {error ? (
                  <div className="h-full flex items-center justify-center">
                    <ErrorState
                      title={t('calendar.agenda.error.title')}
                      description={t('calendar.agenda.error.description')}
                      onAction={() => void refetch()}
                      actionContent={t('calendar.agenda.error.retry')}
                    />
                  </div>
                ) : (
                  <div className="relative h-full">
                    <TasksCalendar
                      tasks={tasks}
                      onDateRangeChange={handleCalendarDateRangeChange}
                    />

                    {isFetching && (
                      <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                        <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-lg">
                          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                          <Text
                            size="small"
                            weight="medium"
                            className="text-gray-700"
                          >
                            {t('calendar.agenda.loading')}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
};
